import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  Languages, 
  BookOpen, 
  CheckCircle2, 
  Clock, 
  ArrowRight, 
  FileText, 
  Globe2, 
  ChevronDown, 
  ChevronUp,
  Sparkles,
  ShieldCheck
} from 'lucide-react';
import { TranslationProject, TranslationStage } from '@ftt/shared';
import { TRANSLATION_STAGES_CONFIG } from '@ftt/shared';

export const ClientTranslationsPage: React.FC = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: translationsRes, isLoading } = useQuery({
    queryKey: ['client-translations'],
    queryFn: () => api.get('/client/translations').then((res) => res.data.data),
  });

  const translations: TranslationProject[] = translationsRes || [];

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-8 font-sans">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-600 font-mono uppercase tracking-widest font-bold">
              VERNACULAR INITIATIVE
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-500 font-serif italic">
              Indian Languages Translation Pipeline
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900 flex items-center gap-2.5">
            <Languages className="w-7 h-7 text-brand-600" />
            <span>Theological Translation Progress</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600">
            Track chapter-level drafting, theological review, and editorial approval for your translated works.
          </p>
        </div>
      </div>

      {/* Translations List */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-500">Loading translation projects...</div>
      ) : translations.length === 0 ? (
        <div className="p-12 rounded-2xl bg-white border border-editorial-border text-center space-y-4">
          <Languages className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-800">No Translation Projects Found</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            You do not currently have any active vernacular translation projects assigned to your account.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {translations.map((proj) => {
            const stageConfig = TRANSLATION_STAGES_CONFIG.find((s) => s.stage === proj.stage) || { label: proj.stage, order: 1, description: '' };
            const isExpanded = expandedId === proj.id;

            return (
              <div
                key={proj.id}
                className="rounded-2xl bg-white border border-editorial-border shadow-xs hover:border-brand-300 transition-all overflow-hidden"
              >
                {/* Main Card Header */}
                <div className="p-6 sm:p-8 space-y-6">
                  
                  {/* Top Row: Code & Languages */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 font-bold uppercase">
                        {proj.projectCode}
                      </span>
                      <span className="text-xs font-mono font-bold text-brand-700 bg-brand-50 px-2.5 py-0.5 rounded-md border border-brand-200 flex items-center gap-1">
                        <Globe2 className="w-3 h-3" />
                        <span>{proj.sourceLanguage} &rarr; {proj.targetLanguage}</span>
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase text-slate-400">Current Phase:</span>
                      <span className="text-xs font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-md border border-slate-200">
                        {stageConfig.label} (Stage {stageConfig.order}/10)
                      </span>
                    </div>
                  </div>

                  {/* Title & Author Info */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-2">
                      <h2 className="font-serif text-2xl font-bold text-slate-900 leading-tight">
                        {proj.targetTitle}
                      </h2>
                      <div className="text-xs text-slate-600">
                        Original English Title: <strong className="text-slate-800 font-serif italic">{proj.sourceTitle}</strong>
                      </div>
                      <div className="text-xs text-slate-500 font-mono">
                        Original Author: <strong className="text-slate-700">{proj.originalAuthor}</strong>
                        {proj.originalPublisher && <span> &bull; Rights: {proj.originalPublisher}</span>}
                      </div>
                    </div>

                    {/* Progress Bar & Word Count */}
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3 flex flex-col justify-center">
                      <div className="flex items-center justify-between text-xs font-mono">
                        <span className="text-slate-500">Overall Progress</span>
                        <span className="font-bold text-brand-700 text-sm">{proj.progressPercentage}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="bg-brand-600 h-2.5 rounded-full transition-all duration-500"
                          style={{ width: `${proj.progressPercentage}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-[11px] font-mono text-slate-500 pt-1">
                        <span>Chapters: <strong>{proj.completedChapters} / {proj.totalChapters}</strong></span>
                        <span>Words: <strong>{proj.translatedWords.toLocaleString()} / {proj.totalWords.toLocaleString()}</strong></span>
                      </div>
                    </div>
                  </div>

                  {/* Personnel & Key Metadata */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100 text-xs font-mono">
                    <div className="p-3 rounded-lg bg-slate-50/60 border border-slate-100">
                      <span className="text-slate-400 block text-[10px] uppercase">Assigned Translator</span>
                      <span className="font-bold text-slate-800">{proj.assignedTranslatorName || 'Vernacular Team'}</span>
                    </div>

                    <div className="p-3 rounded-lg bg-slate-50/60 border border-slate-100">
                      <span className="text-slate-400 block text-[10px] uppercase">Theological Reviewer</span>
                      <span className="font-bold text-slate-800">{proj.theologicalReviewerName || 'Theological Board'}</span>
                    </div>

                    <div className="p-3 rounded-lg bg-slate-50/60 border border-slate-100">
                      <span className="text-slate-400 block text-[10px] uppercase">Target Publication</span>
                      <span className="font-bold text-brand-700">
                        {proj.targetPubDate ? new Date(proj.targetPubDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Q4 2026'}
                      </span>
                    </div>
                  </div>

                  {/* Toggle Chapter Breakdown */}
                  <div className="pt-2 flex justify-end">
                    <button
                      onClick={() => toggleExpand(proj.id)}
                      className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors flex items-center gap-1.5"
                    >
                      <span>{isExpanded ? 'Hide Chapter Checklist' : 'View Chapter-by-Chapter Checklist'}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>

                </div>

                {/* Expanded Chapter-by-Chapter Progress Drawer */}
                {isExpanded && (
                  <div className="bg-slate-50 border-t border-slate-200 p-6 sm:p-8 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-serif font-bold text-base text-slate-900 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-brand-600" />
                        <span>Chapter Progress Checklist ({proj.chapters?.length || 0} Chapters)</span>
                      </h4>
                      <span className="text-xs text-slate-500 font-mono">
                        Theological Review &amp; Approval Milestones
                      </span>
                    </div>

                    <div className="space-y-3">
                      {proj.chapters?.map((chapter) => (
                        <div
                          key={chapter.chapterNumber}
                          className="p-4 rounded-xl bg-white border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-slate-100 font-mono font-bold text-slate-700 text-xs flex items-center justify-center">
                                {chapter.chapterNumber}
                              </span>
                              <span className="font-bold text-sm text-slate-900">
                                {chapter.chapterTitle}
                              </span>
                            </div>
                            <span className="text-[11px] text-slate-500 font-mono block pl-8">
                              Word Count: {chapter.wordCount.toLocaleString()} words
                            </span>
                          </div>

                          {/* 3-Step Milestone Status Badges */}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 pl-8 sm:pl-0">
                            <div className={`px-2.5 py-1 rounded-md text-[10px] font-mono font-bold flex items-center gap-1 ${
                              chapter.draftCompleted ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-400'
                            }`}>
                              <CheckCircle2 className="w-3 h-3" />
                              <span>Drafting</span>
                            </div>

                            <div className={`px-2.5 py-1 rounded-md text-[10px] font-mono font-bold flex items-center gap-1 ${
                              chapter.theologicalReviewCompleted ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-400'
                            }`}>
                              <ShieldCheck className="w-3 h-3" />
                              <span>Theological Review</span>
                            </div>

                            <div className={`px-2.5 py-1 rounded-md text-[10px] font-mono font-bold flex items-center gap-1 ${
                              chapter.editorApproved ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-400'
                            }`}>
                              <Sparkles className="w-3 h-3" />
                              <span>Editorial Approval</span>
                            </div>
                          </div>

                        </div>
                      ))}
                    </div>

                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};
